import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button, ComboBox, Input, Label, ListBox, ListBoxItem, Popover } from 'react-aria-components'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { type DataSourceSearchItem, requestCenterApi } from '@/features/request-center'
import { Icon } from '@/shared/components/atoms/Icon'
import { cn } from '@/lib/utils'

interface BaseProps {
	dataSourceKey: string
	label: string
	required?: boolean
	disabled?: boolean
	description?: string
	debounceMs?: number
	pageLimit?: number
}

interface SingleProps extends BaseProps {
	multi?: false
	value: string | undefined
	onChange: (next: string | undefined) => void
	/**
	 * Callback fired alongside `onChange` carrying the human-readable label
	 * for the picked value (or `undefined` when cleared). Used by the form
	 * renderer to build the `formDataLabels` snapshot that ships with the
	 * submit payload — backend persists it on `formData.__labels` so the
	 * row summary in /my, dashboard widgets, and the digest renders
	 * "GitHub" instead of a UUID, and stays stable if the provider is
	 * later renamed. Optional — fields wired in stand-alone contexts
	 * (admin views, search popovers) don't need to provide it.
	 */
	onLabelChange?: (label: string | undefined) => void
}

interface MultiProps extends BaseProps {
	multi: true
	value: string[]
	onChange: (next: string[]) => void
	/** Multi-select counterpart of `onLabelChange`. Receives the labels in submission order. */
	onLabelChange?: (labels: string[]) => void
}

export type AutocompleteFieldProps = SingleProps | MultiProps

const isMulti = (p: AutocompleteFieldProps): p is MultiProps => p.multi === true

const DEFAULT_DEBOUNCE_MS = 300
const DEFAULT_PAGE_LIMIT = 10

/**
 * Live-search dropdown bound to a registered data source. Single-select uses
 * react-aria-components' ComboBox (full keyboard support, screen-reader
 * friendly); multi-select renders the same ComboBox plus a chip row above
 * the input.
 *
 * Server-side search: the user's input is debounced (default 300ms) and sent
 * as the `q` query parameter on every stable input. The initial mount fires
 * one fetch with `q=''` so the dropdown is non-empty on first open; the
 * configured `pageLimit` (default 10) bounds every fetch.
 *
 * Styling matches the shadcn `Input` so the field blends with the rest of
 * the form chrome.
 */
export function AutocompleteField(props: AutocompleteFieldProps) {
	const debounceMs = props.debounceMs ?? DEFAULT_DEBOUNCE_MS
	const pageLimit = props.pageLimit ?? DEFAULT_PAGE_LIMIT

	const [inputValue, setInputValue] = useState('')
	const debouncedInput = useDebouncedValue(inputValue, debounceMs)

	const selectedValues = useMemo(() => {
		if (isMulti(props)) return props.value
		return props.value ? [props.value] : []
	}, [props])

	const {
		allItems,
		isLoading,
		isFetching,
		error,
		labelFor: baseLabelFor,
	} = useDataSourceItems(props.dataSourceKey, debouncedInput, pageLimit, selectedValues)

	const { mergedItems, labelFor } = useMergedItemsWithSelection(allItems, selectedValues, baseLabelFor, inputValue)

	// First render holds the skeleton until items load. Preserves the
	// stale-raw-ID-display guard noted in observation #522 — selected
	// values can render as raw UUIDs if the ComboBox mounts before
	// `labelFor` has a hit for them.
	if (isLoading && allItems.length === 0) {
		return (
			<div className="space-y-1">
				<FieldLabel label={props.label} required={props.required} />
				<div className={cn(INPUT_CLASS, 'pr-9 text-muted-foreground')}>Loading…</div>
				{props.description ? <p className="text-xs text-muted-foreground">{props.description}</p> : null}
			</div>
		)
	}

	return (
		<div className="space-y-1">
			<MountedComboBox
				props={props}
				items={mergedItems}
				labelFor={labelFor}
				inputValue={inputValue}
				setInputValue={setInputValue}
				isFetching={isFetching}
				selectedValues={isMulti(props) ? props.value : []}
				itemsLoaded={allItems.length > 0 || !isLoading}
			/>
			{props.description ? <p className="text-xs text-muted-foreground">{props.description}</p> : null}
			{error ? <p className="text-xs text-destructive">{error}</p> : null}
		</div>
	)
}

interface MountedProps {
	props: AutocompleteFieldProps
	items: CollectionItem[]
	labelFor: (v: string) => string
	inputValue: string
	setInputValue: (v: string) => void
	isFetching: boolean
	selectedValues: string[]
	itemsLoaded: boolean
}

function MountedComboBox({
	props,
	items,
	labelFor,
	inputValue,
	setInputValue,
	isFetching,
	selectedValues,
	itemsLoaded,
}: MountedProps) {
	const { handleSelectionChange, removeChip, selectedKey } = useComboBoxHandlers(
		props,
		items,
		labelFor,
		inputValue,
		setInputValue,
		itemsLoaded,
	)

	return (
		<ComboBox
			items={items}
			inputValue={inputValue}
			onInputChange={setInputValue}
			onSelectionChange={handleSelectionChange}
			selectedKey={selectedKey}
			isDisabled={props.disabled}
			allowsEmptyCollection
			menuTrigger="focus"
			className="relative flex flex-col gap-1"
		>
			<FieldLabel label={props.label} required={props.required} />
			{isMulti(props) && selectedValues.length > 0 ? (
				<ChipRow values={selectedValues} labelFor={labelFor} onRemove={removeChip} />
			) : null}
			<Input placeholder="Type to search…" className={cn(INPUT_CLASS, 'pr-9')} />
			<TriggerAdornments showSpinner={isFetching && inputValue !== ''} />
			<SuggestionsPopover />
		</ComboBox>
	)
}

function TriggerAdornments({ showSpinner }: { showSpinner: boolean }) {
	return (
		<>
			{showSpinner ? (
				<div
					aria-hidden
					data-testid="autocomplete-spinner"
					className="absolute right-9 bottom-3 h-3 w-3 animate-spin rounded-full border border-muted-foreground border-t-transparent"
				/>
			) : null}
			<Button
				aria-label="Open suggestions"
				className="absolute right-0 bottom-0 flex h-10 items-center px-2 text-muted-foreground"
			>
				<Icon name="ChevronDown" size="sm" aria-hidden />
			</Button>
		</>
	)
}

function useExternalValueSync(
	externalValue: string | undefined,
	labelFor: (v: string) => string,
	inputValue: string,
	setInputValue: (v: string) => void,
	itemsLoaded: boolean,
	isMultiMode: boolean,
) {
	// Keep the visible input in sync with externally controlled values, such as
	// autofill, without overwriting active user typing on refetch. Tracks both
	// the externalValue and the label so a hydration fetch that resolves a
	// previously-raw label can update the input — unless the user has typed
	// something different over it.
	const lastSyncedExternalValueRef = useRef<string | undefined>(undefined)
	const lastSyncedLabelRef = useRef<string | undefined>(undefined)
	const initializedRef = useRef(false)
	const inputValueRef = useRef(inputValue)
	inputValueRef.current = inputValue

	useEffect(() => {
		if (isMultiMode) return
		if (!itemsLoaded) return
		const newLabel = externalValue ? labelFor(externalValue) : ''
		const valueChanged = lastSyncedExternalValueRef.current !== externalValue

		if (!initializedRef.current || valueChanged) {
			initializedRef.current = true
			lastSyncedExternalValueRef.current = externalValue
			lastSyncedLabelRef.current = newLabel
			setInputValue(newLabel)
			return
		}

		if (lastSyncedLabelRef.current === newLabel) return
		// Label changed for the same externalValue (e.g. hydration resolved a
		// previously-raw label). Only push it into the input when the user
		// hasn't typed something different over the last-synced label.
		if (inputValueRef.current === lastSyncedLabelRef.current) {
			setInputValue(newLabel)
		}
		lastSyncedLabelRef.current = newLabel
	}, [externalValue, labelFor, isMultiMode, setInputValue, itemsLoaded])
}

function useComboBoxHandlers(
	props: AutocompleteFieldProps,
	items: CollectionItem[],
	labelFor: (v: string) => string,
	inputValue: string,
	setInputValue: (v: string) => void,
	itemsLoaded: boolean,
) {
	useExternalValueSync(
		!isMulti(props) ? props.value : undefined,
		labelFor,
		inputValue,
		setInputValue,
		itemsLoaded,
		props.multi === true,
	)

	const handleSelectionChange = (val: string | number | null) => {
		if (val === null) {
			if (!isMulti(props)) {
				props.onChange(undefined)
				props.onLabelChange?.(undefined)
			}
			setInputValue('')
			return
		}
		const stringVal = String(val)
		const pickedLabel = items.find((it) => it.value === stringVal)?.label ?? labelFor(stringVal)
		if (isMulti(props)) {
			if (!props.value.includes(stringVal)) {
				const nextValues = [...props.value, stringVal]
				props.onChange(nextValues)
				props.onLabelChange?.(nextValues.map((v) => items.find((it) => it.value === v)?.label ?? labelFor(v)))
			}
			setInputValue('')
		} else {
			props.onChange(stringVal)
			props.onLabelChange?.(pickedLabel)
			setInputValue(pickedLabel)
		}
	}

	const removeChip = (val: string) => {
		if (isMulti(props)) {
			const nextValues = props.value.filter((v) => v !== val)
			props.onChange(nextValues)
			props.onLabelChange?.(nextValues.map((v) => items.find((it) => it.value === v)?.label ?? labelFor(v)))
		}
	}

	const selectedKey = isMulti(props) ? null : (props.value ?? null)

	return { handleSelectionChange, removeChip, selectedKey }
}

// ─── Sub-components (extracted to keep AutocompleteField scannable) ──────────

function FieldLabel({ label, required }: { label: string; required?: boolean }) {
	return (
		<Label className="text-sm font-medium leading-none text-foreground">
			{label}
			{required ? (
				<span aria-hidden className="ml-0.5 text-destructive">
					*
				</span>
			) : null}
		</Label>
	)
}

function ChipRow({
	values,
	labelFor,
	onRemove,
}: {
	values: string[]
	labelFor: (v: string) => string
	onRemove: (v: string) => void
}) {
	return (
		<div className="flex flex-wrap gap-1 pt-1">
			{values.map((v) => (
				<span
					key={v}
					className="inline-flex items-center gap-1 rounded-md border border-input bg-muted px-2 py-0.5 text-xs"
				>
					{labelFor(v)}
					<button
						type="button"
						aria-label={`Remove ${labelFor(v)}`}
						className="text-muted-foreground hover:text-foreground"
						onClick={() => onRemove(v)}
					>
						<Icon name="X" size={12} />
					</button>
				</span>
			))}
		</div>
	)
}

const INPUT_CLASS = cn(
	'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background',
	'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
	'disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
)

function SuggestionsPopover() {
	return (
		<Popover
			placement="bottom start"
			offset={4}
			shouldFlip={false}
			className="z-50 max-h-60 w-[var(--trigger-width)] overflow-auto rounded-md border border-input bg-popover text-popover-foreground shadow-md"
		>
			<ListBox className="p-1 outline-none">
				{(item: CollectionItem) => (
					<ListBoxItem
						textValue={item.label}
						className="cursor-pointer rounded-sm px-2 py-1.5 text-sm outline-none data-[focused]:bg-accent data-[focused]:text-accent-foreground hover:bg-accent hover:text-accent-foreground"
					>
						{item.label}
					</ListBoxItem>
				)}
			</ListBox>
		</Popover>
	)
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

type CollectionItem = DataSourceSearchItem & { id: string }

interface DataSourceItemsHook {
	allItems: CollectionItem[]
	isLoading: boolean
	isFetching: boolean
	error: string | null
	labelFor: (v: string) => string
}

const HYDRATION_LIMIT = 50

const addId = (it: DataSourceSearchItem): CollectionItem => ({ ...it, id: it.value })

/**
 * Hydration fetch: when the field is mounted with externally-set values that
 * aren't in the typed-search response, fire one wider fetch so `labelFor` can
 * resolve their labels. Cached forever per data source (the worst that
 * happens if the hydration result goes stale is a brief raw-UUID flash on
 * the next mount, fixed on the next refetch). Bounded by the backend's
 * `SEARCH_LIMIT_MAX` (currently 50).
 */
function useDataSourceHydration(
	dataSourceKey: string,
	selectedValues: string[],
	primaryItems: CollectionItem[],
): CollectionItem[] {
	const needsHydration = useMemo(() => {
		if (selectedValues.length === 0) return false
		const present = new Set(primaryItems.map((it) => it.value))
		return selectedValues.some((v) => !present.has(v))
	}, [selectedValues, primaryItems])

	const { data } = useQuery({
		queryKey: ['data-source-hydrate', dataSourceKey, HYDRATION_LIMIT],
		queryFn: () => requestCenterApi.searchDataSource(dataSourceKey, { limit: HYDRATION_LIMIT }),
		enabled: needsHydration,
		staleTime: Infinity,
	})

	return useMemo(() => (data?.data ?? []).map(addId), [data])
}

/**
 * Server-side search backed by `GET /requests/data-sources/:key/search`. The
 * debounced `q` is forwarded as the query parameter; `pageLimit` bounds the
 * result count end-to-end. Empty `q` returns the directory head so the
 * dropdown is non-empty before the user types. `placeholderData` keeps the
 * previous result rendered while a new debounced search is in flight, which
 * avoids list flicker between keystrokes.
 *
 * When `selectedValues` contains values not present in the search response,
 * a one-shot hydration fetch resolves their labels so externally-set
 * (autofilled) values render as the human-readable label instead of a raw
 * UUID.
 */
function useDataSourceItems(
	dataSourceKey: string,
	q: string,
	pageLimit: number,
	selectedValues: string[],
): DataSourceItemsHook {
	const { data, isLoading, isFetching, error } = useQuery({
		queryKey: ['data-source-search', dataSourceKey, q, pageLimit],
		queryFn: () => requestCenterApi.searchDataSource(dataSourceKey, { q: q === '' ? undefined : q, limit: pageLimit }),
		staleTime: 5 * 60 * 1000,
		placeholderData: (prev) => prev,
	})

	const primaryItems = useMemo(() => (data?.data ?? []).map(addId), [data])
	const hydrationItems = useDataSourceHydration(dataSourceKey, selectedValues, primaryItems)

	const allItems = useMemo(() => {
		if (hydrationItems.length === 0) return primaryItems
		const present = new Set(primaryItems.map((it) => it.value))
		const extras = hydrationItems.filter((it) => !present.has(it.value))
		return extras.length === 0 ? primaryItems : [...primaryItems, ...extras]
	}, [primaryItems, hydrationItems])

	const labelCache = useMemo(() => {
		const map: Record<string, string> = {}
		for (const it of allItems) map[it.value] = it.label
		return map
	}, [allItems])

	const labelFor = useCallback((val: string): string => labelCache[val] ?? val, [labelCache])

	return {
		allItems,
		isLoading,
		isFetching,
		error: error ? (error instanceof Error ? error.message : 'Search failed') : null,
		labelFor,
	}
}

/**
 * Keeps the currently-selected entries available for chip label resolution
 * and for the controlled `selectedKey` even when a subsequent search response
 * omits them. The snapshot extras are merged into the items collection only
 * when the typed input is empty or when the extra's label matches the typed
 * input — react-aria-components skips its built-in filter under controlled
 * `items`, so we apply the substring match here to avoid leaking previously
 * picked options into a typed-search listbox.
 */
interface MergedItemsHook {
	mergedItems: CollectionItem[]
	labelFor: (v: string) => string
}

function useSelectionSnapshot(allItems: CollectionItem[], selectedValues: string[]) {
	const [snapshot, setSnapshot] = useState<Map<string, CollectionItem>>(new Map())

	useEffect(() => {
		setSnapshot((prev) => {
			let changed = false
			const next = new Map(prev)
			for (const k of Array.from(next.keys())) {
				if (!selectedValues.includes(k)) {
					next.delete(k)
					changed = true
				}
			}
			// Populate the snapshot only with entries we've actually seen in a
			// server response. Synthesising a raw-value entry during the loading
			// phase would poison `labelFor` for the rest of the lifecycle.
			for (const v of selectedValues) {
				const fresh = allItems.find((it) => it.value === v)
				if (!fresh) continue
				const current = next.get(v)
				if (current?.value !== fresh.value || current?.label !== fresh.label) {
					next.set(v, fresh)
					changed = true
				}
			}
			return changed ? next : prev
		})
	}, [allItems, selectedValues])

	return snapshot
}

function useMergedItemsWithSelection(
	allItems: CollectionItem[],
	selectedValues: string[],
	baseLabelFor: (v: string) => string,
	inputValue: string,
): MergedItemsHook {
	const snapshot = useSelectionSnapshot(allItems, selectedValues)

	const mergedItems = useMemo(() => {
		const inputLower = inputValue.toLowerCase()
		const allFiltered =
			inputValue === '' ? allItems : allItems.filter((it) => it.label.toLowerCase().includes(inputLower))
		if (snapshot.size === 0) return allFiltered
		const present = new Set(allFiltered.map((it) => it.value))
		const extras: CollectionItem[] = []
		for (const [val, item] of snapshot) {
			if (present.has(val)) continue
			if (inputValue === '' || item.label.toLowerCase().includes(inputLower)) {
				extras.push(item)
			}
		}
		return extras.length === 0 ? allFiltered : [...extras, ...allFiltered]
	}, [allItems, snapshot, inputValue])

	// labelFor prefers the latest server result (most current data) and falls back
	// to the snapshot (which keeps the picked option's label across searches that
	// no longer include it) and finally to the cache-miss raw value.
	const labelFor = useCallback(
		(val: string): string => {
			const server = allItems.find((it) => it.value === val)
			if (server) return server.label
			const snap = snapshot.get(val)
			if (snap) return snap.label
			return baseLabelFor(val)
		},
		[allItems, snapshot, baseLabelFor],
	)

	return { mergedItems, labelFor }
}
